import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Plus, Trash2, Loader2, Pencil, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';

export default function DescriptionTemplatesPanel({ open, onOpenChange, descTemplates, fetchTemplates }) {
  const confirm = useConfirm();
  const [tplEdit, setTplEdit] = useState(null);
  const [tplSaving, setTplSaving] = useState(false);

  const saveTemplate = async () => {
    if (!tplEdit.name.trim()) return toast.error('Template name is required');
    setTplSaving(true);
    try {
      if (tplEdit.id === 'new') {
        await api.post('/proposal-description-templates', { name: tplEdit.name.trim(), content: tplEdit.content });
        toast.success('Template added');
      } else {
        await api.put(`/proposal-description-templates/${tplEdit.id}`, { name: tplEdit.name.trim(), content: tplEdit.content });
        toast.success('Template saved');
      }
      setTplEdit(null);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save template'); }
    setTplSaving(false);
  };
  const setTemplateDefault = async (id) => {
    try { await api.put(`/proposal-description-templates/${id}/set-default`); await fetchTemplates(); toast.success('Default template updated'); }
    catch { toast.error('Could not set default'); }
  };
  const deleteTemplate = async (id) => {
    const ok = await confirm({ title: 'Delete template', description: 'This template will be removed. This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try { await api.delete(`/proposal-description-templates/${id}`); await fetchTemplates(); toast.success('Template deleted'); }
    catch { toast.error('Could not delete template'); }
  };

  const TemplateEditor = () => (
    <div className="space-y-2">
      <Input value={tplEdit.name} onChange={e => setTplEdit({ ...tplEdit, name: e.target.value })} placeholder="Template name" className="bg-background border-border h-8 text-sm" data-testid="template-edit-name" />
      <Textarea value={tplEdit.content} onChange={e => setTplEdit({ ...tplEdit, content: e.target.value })} rows={5} placeholder="Description content" className="bg-background border-border text-sm" data-testid="template-edit-content" />
      <div className="flex items-center gap-2">
        <Button size="sm" className="bg-primary text-primary-foreground" disabled={tplSaving} onClick={saveTemplate} data-testid="template-save-btn">{tplSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}</Button>
        <Button size="sm" variant="outline" className="border-border" onClick={() => setTplEdit(null)} data-testid="template-cancel-btn">Cancel</Button>
      </div>
    </div>
  );

  const templates = Array.isArray(descTemplates) ? descTemplates : [];

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTplEdit(null); }}>
      <SheetContent className="bg-card border-border w-full sm:max-w-lg overflow-y-auto" data-testid="templates-panel">
        <SheetHeader>
          <SheetTitle className="font-medium" style={{ fontSize: '14px', color: '#010A21' }}>Description Templates</SheetTitle>
          <SheetDescription>Reusable cover descriptions. The default pre-fills new proposals; changing it does not affect existing proposals.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {templates.map(t => {
            const editing = tplEdit && tplEdit.id === t.id;
            return (
              <div key={t.id} className="border border-border rounded-md p-3" data-testid={`template-row-${t.id}`}>
                {editing ? TemplateEditor() : (
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium truncate" style={{ color: '#010A21' }} data-testid={`template-name-${t.id}`}>{t.name}</span>
                          {t.is_default && <span style={{ backgroundColor: '#EFF6FF', color: '#1B4A76', borderRadius: '2px', fontSize: '12px', padding: '1px 6px' }} data-testid={`template-default-pill-${t.id}`}>Default</span>}
                        </div>
                        <p className="text-xs mt-1" style={{ color: '#031333' }}>{(t.content || '').slice(0, 100)}{(t.content || '').length > 100 ? '...' : ''}</p>
                        {!t.is_default && <Button size="sm" variant="ghost" className="mt-1 h-7 px-2 text-xs" style={{ color: '#1B4A76' }} onClick={() => setTemplateDefault(t.id)} data-testid={`template-set-default-${t.id}`}><Star className="h-3.5 w-3.5 mr-1" /> Set as Default</Button>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTplEdit({ id: t.id, name: t.name, content: t.content || '' })} data-testid={`template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteTemplate(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {templates.length === 0 && !tplEdit && <p className="text-sm text-muted-foreground py-4 text-center">No templates yet.</p>}
          {tplEdit && tplEdit.id === 'new' && (
            <div className="border border-border rounded-md p-3" data-testid="template-new-row">{TemplateEditor()}</div>
          )}
          {!(tplEdit && tplEdit.id === 'new') && (
            <Button variant="outline" className="w-full border-border" onClick={() => setTplEdit({ id: 'new', name: '', content: '' })} data-testid="add-template-btn"><Plus className="h-4 w-4 mr-1" /> Add Template</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
